/**
 * Search results page. The assistant dialog is the primary search surface; this
 * page exists so a search is linkable, indexable and works without JavaScript
 * dialogs (e.g. arriving from an external link with ?q=).
 */
import { clear, el } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import type { RouteMatch } from "../core/routes.js";
import { applyPageSeo } from "../core/seo.js";
import { breadcrumbs, pill } from "../components/common.js";
import type { SearchHit } from "../data/types.js";
import { plainText } from "../data/typography.js";

// Synchronous shell: the results region fills in asynchronously (see `render`).
export function searchView(ctx: AppContext, match: RouteMatch): HTMLElement {
	const copy = ctx.copy();
	// A phrase pasted from the page carries U+00A0; search and track plain words.
	const query = plainText(match.query.q ?? "");

	applyPageSeo(ctx, {
		title: `${ctx.t("nav.search")} — ${copy.brand.name}`,
		description: copy.assistant.subtitle,
		noindex: true,
	});

	const results = el("div", { class: "mb-searchresults", "aria-live": "polite" });

	const input = el("input", {
		class: "mb-field__input",
		type: "search",
		name: "q",
		value: query,
		placeholder: ctx.t("common.searchPlaceholder"),
		"aria-label": ctx.t("nav.search"),
	});

	const form = el(
		"form",
		{
			class: "mb-searchform",
			onSubmit: (event: Event) => {
				event.preventDefault();
				ctx.navigate("search", undefined, { q: input.value.trim() });
			},
		},
		input,
		el("button", { class: "mb-cta mb-cta--accent", type: "submit" }, ctx.t("nav.search")),
	);

	async function render(): Promise<void> {
		clear(results);
		if (query.trim() === "") {
			results.appendChild(el("p", { class: "mb-searchresults__empty" }, copy.search.emptyHint));
			return;
		}

		const result = await ctx.gateway.search(query);
		ctx.track({ type: "search", query, results: result.total });

		results.appendChild(
			el(
				"p",
				{ class: "mb-searchresults__count" },
				// A replacer function: `$&` or `{total}` typed by the user stays literal.
				copy.search.resultsCount.replace(/\{(query|total)\}/g, (_, name: string) =>
					name === "query" ? query : String(result.total),
				),
			),
		);

		if (result.hits.length === 0) {
			results.appendChild(
				el("p", { class: "mb-searchresults__empty" }, copy.assistant.answerEmpty),
			);
			if (result.suggestions.length > 0) {
				results.appendChild(
					el(
						"div",
						{ class: "mb-filters" },
						...result.suggestions.map((suggestion) =>
							el(
								"a",
								{ class: "mb-filter", href: ctx.href("search", undefined, { q: suggestion }) },
								suggestion,
							),
						),
					),
				);
			}
			return;
		}

		const list = el("ul", { class: "mb-hitlist" });
		for (const hit of result.hits) list.appendChild(hitItem(ctx, hit));
		results.appendChild(list);
	}

	void render();

	return el(
		"div",
		{ class: "mb-view mb-view--search" },
		el(
			"section",
			{ class: "mb-section mb-section--white" },
			el(
				"div",
				{ class: "mb-section__inner" },
				breadcrumbs(ctx, [{ label: copy.nav.start, key: "home" }, { label: ctx.t("nav.search") }]),
				el("h1", { class: "mb-pagehead__title" }, ctx.t("nav.search")),
				form,
				results,
			),
		),
	);
}

function hitItem(ctx: AppContext, hit: SearchHit): HTMLElement {
	const isEn = ctx.locale() === "en";
	const typeLabels: Record<string, string> = isEn
		? { service: "Service", case_study: "Case study", article: "Insight", company: "Company" }
		: { service: "Usługa", case_study: "Realizacja", article: "Analiza", company: "Spółka" };

	return el(
		"li",
		{ class: "mb-hitlist__item" },
		el(
			"a",
			{ class: "mb-hitlist__link", href: hit.url },
			pill(typeLabels[hit.contentType] ?? hit.contentType, "neutral"),
			el("span", { class: "mb-hitlist__title" }, hit.title),
			el("span", { class: "mb-hitlist__snippet" }, hit.snippet),
		),
	);
}
